import React from 'react';
import { ChevronDownIcon, ChevronUpIcon, ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { requiredPlaceholdersOf } from '../utils/promptValidation';

export const ModelOption = ({ model, selected, onSelect }) => (
  <label
    className={`block cursor-pointer rounded-md border px-3 py-2 ${
      selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
    }`}
  >
    <div className="flex items-start gap-2">
      <input
        type="radio"
        name="report-llm-model"
        value={model.value}
        checked={selected}
        onChange={onSelect}
        className="mt-1"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">
          {model.label}
          <span className="ml-2 text-xs font-normal text-gray-500">{model.provider}</span>
          {model.isDefault && (
            <span className="ml-2 text-xs font-normal text-blue-700">default</span>
          )}
        </div>
        <div className="mt-1 text-xs text-gray-700">
          Context {model.contextWindow || 'n/a'}
          {' · '}
          Output {model.maxOutput || 'n/a'}
          {model.inputPrice && model.outputPrice && (
            <>
              {' · '}
              in {model.inputPrice}
              {' · '}
              out {model.outputPrice}
            </>
          )}
        </div>
        {model.capabilities?.length > 0 && (
          <div className="mt-1 text-xs text-gray-500">
            {model.capabilities.join(' · ')}
          </div>
        )}
        {model.description && (
          <p className="mt-1 text-xs text-gray-600">{model.description}</p>
        )}
      </div>
    </div>
  </label>
);

export const OptionCheckbox = ({ checked, onToggle, Icon, title, children }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1" />
    <div>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <Icon className="h-4 w-4" />
        {title}
      </div>
      <p className="text-xs text-gray-600 mt-1">{children}</p>
    </div>
  </label>
);

const PromptErrors = ({ errors }) => (
  <div className="mt-2 flex items-start gap-2 text-xs text-red-600">
    <ExclamationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
    <div>
      {errors.map((error, idx) => (
        <div key={idx}>{error}</div>
      ))}
    </div>
  </div>
);

const PromptHeader = ({ promptConfig, isEnabled, isExpanded, errors, onToggleExpanded, onToggleEnabled }) => {
  const placeholders = requiredPlaceholdersOf(promptConfig);
  return (
    <div
      className="bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={onToggleExpanded}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="font-medium text-sm text-gray-900">
            {promptConfig.name}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {promptConfig.description}
          </div>
        </div>
        <div className="text-gray-400 ml-4">
          {isExpanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
        </div>
      </div>

      {placeholders.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          <span className="text-xs text-gray-500">Required placeholders:</span>
          {placeholders.map((placeholder) => (
            <span key={placeholder} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              {placeholder}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        {/* Clicking the checkbox must not also expand / collapse the card. */}
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={onToggleEnabled}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
          <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
            Enable to edit
          </span>
        </div>
      </div>

      {errors.length > 0 && <PromptErrors errors={errors} />}
    </div>
  );
};

const PromptEditor = ({ promptText, canReset, onChange, onReset }) => (
  <>
    <textarea
      value={promptText}
      onChange={(e) => onChange(e.target.value)}
      rows={12}
      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:ring-blue-500 focus:border-blue-500"
      placeholder="Enter custom prompt text..."
    />
    <div className="flex justify-end">
      <button
        onClick={onReset}
        disabled={!canReset}
        className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
      >
        <ArrowPathIcon className="h-3 w-3" />
        Reset to Default
      </button>
    </div>
  </>
);

const PromptPreview = ({ promptText }) => (
  <>
    <textarea
      value={promptText}
      readOnly
      rows={12}
      className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm font-mono bg-gray-50 text-gray-700 cursor-not-allowed"
      placeholder="Enable checkbox above to edit this prompt"
    />
    <div className="text-xs text-gray-500 italic flex items-center gap-1">
      <ExclamationCircleIcon className="h-4 w-4" />
      Check the "Enable to edit" checkbox above to customize this prompt. The default prompt will be used otherwise.
    </div>
  </>
);

/** One editable prompt: header with toggles, and the prompt text when expanded. */
export const PromptCard = ({
  promptConfig,
  promptText,
  isEnabled,
  isExpanded,
  errors,
  canReset,
  onToggleExpanded,
  onToggleEnabled,
  onChange,
  onReset,
}) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <PromptHeader
      promptConfig={promptConfig}
      isEnabled={isEnabled}
      isExpanded={isExpanded}
      errors={errors}
      onToggleExpanded={onToggleExpanded}
      onToggleEnabled={onToggleEnabled}
    />
    {isExpanded && (
      <div className="p-4 bg-white border-t border-gray-200">
        <div className="space-y-3">
          {isEnabled ? (
            <PromptEditor promptText={promptText} canReset={canReset} onChange={onChange} onReset={onReset} />
          ) : (
            <PromptPreview promptText={promptText} />
          )}
        </div>
      </div>
    )}
  </div>
);
